import { useEffect, useState } from "react";
import { Download, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, asset } from "../../lib/api";
import { FRAME_STYLE, fmtDate } from "../../lib/art";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";

const loadImg = (src) => new Promise((ok, bad) => { const i = new Image(); i.crossOrigin = "use-credentials"; i.onload = () => ok(i); i.onerror = bad; i.src = src; });

export const makeCard = async (work, nickname) => {
  const W = 1080, H = 1350;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const x = c.getContext("2d");
  x.fillStyle = "#FFF6E6"; x.fillRect(0, 0, W, H);
  x.fillStyle = "#F7D9B5"; for (let i = 0; i < 40; i++) { x.globalAlpha = 0.25; x.beginPath(); x.arc((i * 137) % W, (i * 251) % H, 6 + (i % 5) * 3, 0, 7); x.fill(); }
  x.globalAlpha = 1;
  const f = FRAME_STYLE[work.frame] || FRAME_STYLE.none;
  const box = 820, bx = (W - box) / 2, by = 150;
  const cols = f.colors.length ? f.colors : ["#ffffff", "#ffffff"];
  const g = x.createLinearGradient(bx, by, bx + box, by + box);
  cols.forEach((col, i) => g.addColorStop(i / Math.max(1, cols.length - 1), col));
  x.shadowColor = "rgba(0,0,0,.18)"; x.shadowBlur = 30; x.shadowOffsetY = 12;
  x.fillStyle = g; x.fillRect(bx, by, box, box);
  x.shadowColor = "transparent";
  const pad = work.frame === "none" ? 0 : 40;
  x.fillStyle = f.inner || "#fff"; x.fillRect(bx + pad - 8, by + pad - 8, box - 2 * pad + 16, box - 2 * pad + 16);
  const img = await loadImg(asset(work.image_url));
  x.drawImage(img, bx + pad, by + pad, box - 2 * pad, box - 2 * pad);
  x.textAlign = "center"; x.fillStyle = "#7a2e12";
  x.font = "900 64px 'Noto Sans TC',sans-serif"; x.fillText(work.title || "我的畫", W / 2, by + box + 100);
  x.fillStyle = "#8a6b55"; x.font = "500 36px 'Noto Sans TC',sans-serif";
  x.fillText([nickname && `${nickname} 的作品`, fmtDate(work.created_at)].filter(Boolean).join("・"), W / 2, by + box + 160);
  x.fillStyle = "#C0392B"; x.font = "900 46px 'Noto Serif TC',serif"; x.fillText("小書童畫坊", W / 2, 95);
  return new Promise((ok) => c.toBlob(ok, "image/png"));
};

export const ShareWorkDialog = ({ work, nickname, onClose }) => {
  const [showName, setShowName] = useState(false);
  const [blob, setBlob] = useState(null);
  const [link, setLink] = useState("");
  useEffect(() => {
    setBlob(null);
    makeCard(work, showName ? nickname : null).then(setBlob).catch(() => toast.error("分享圖製作失敗"));
  }, [work, showName, nickname]);
  const preview = blob ? URL.createObjectURL(blob) : null;
  const getLink = async () => {
    if (link && link.name === showName) return link.url;
    const r = await api.post("/art/shares", { child_id: work.child_id, kind: "work", work_ids: [work.work_id], show_nickname: showName });
    const url = `${window.location.origin}/art/s/${r.data.token}`;
    setLink({ url, name: showName });
    return url;
  };
  const copy = async () => { const u = await getLink(); await navigator.clipboard.writeText(u).catch(() => {}); toast.success("已複製分享連結"); };
  const openSocial = async (base) => { const u = await getLink(); window.open(base + encodeURIComponent(u), "_blank", "noopener"); };
  const download = () => { const a = document.createElement("a"); a.href = preview; a.download = `${work.title || "artwork"}.png`; a.click(); };
  const sysShare = async () => {
    const file = new File([blob], "xiaoshutong-art.png", { type: "image/png" });
    const url = await getLink();
    if (navigator.canShare?.({ files: [file] })) return navigator.share({ files: [file], title: work.title, text: `${work.title}｜小書童畫坊 ${url}` }).catch(() => {});
    if (navigator.share) return navigator.share({ title: work.title, url }).catch(() => {});
    toast("這個瀏覽器不支援系統分享，請改用下載或複製連結");
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-[2rem] max-w-xl bg-white max-h-[92vh] overflow-y-auto" data-testid="share-work-dialog">
        <DialogTitle className="text-2xl font-black">分享作品</DialogTitle>
        <div className="rounded-2xl bg-stone-100 p-3 flex justify-center">{preview ? <img src={preview} alt="" data-testid="share-card-preview" className="max-h-[40vh] rounded-xl shadow" /> : <div className="h-60 flex items-center text-stone-400">製作分享圖中…</div>}</div>
        <label className="flex items-center justify-between py-1"><span>顯示孩子暱稱「{nickname}」</span><Switch data-testid="share-show-name" checked={showName} onCheckedChange={(v) => { setShowName(v); setLink(""); }} /></label>
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="share-system-btn" disabled={!blob} onClick={sysShare} className="h-12 rounded-xl bg-stone-800 text-white flex items-center justify-center gap-2 font-bold"><Share2 size={18} />分享</button>
          <button data-testid="share-download-btn" disabled={!blob} onClick={download} className="h-12 rounded-xl bg-[#E2683C] text-white flex items-center justify-center gap-2 font-bold"><Download size={18} />下載圖片</button>
          <button data-testid="share-copy-btn" onClick={copy} className="h-12 rounded-xl bg-stone-100 flex items-center justify-center gap-2 font-bold"><Link2 size={18} />複製連結</button>
          <button data-testid="share-facebook-btn" onClick={() => openSocial("https://www.facebook.com/sharer/sharer.php?u=")} className="h-12 rounded-xl bg-[#1877F2] text-white font-bold">Facebook</button>
          <button data-testid="share-line-btn" onClick={() => openSocial("https://social-plugins.line.me/lineit/share?url=")} className="h-12 rounded-xl bg-[#06C755] text-white font-bold">LINE</button>
          <button data-testid="share-whatsapp-btn" onClick={() => openSocial("https://wa.me/?text=")} className="h-12 rounded-xl bg-[#25D366] text-white font-bold">WhatsApp</button>
        </div>
        <p className="text-sm text-stone-500" data-testid="share-instagram-hint">Instagram：手機上按「分享」選 Instagram，或先「下載圖片」再到 Instagram 發佈。</p>
      </DialogContent>
    </Dialog>
  );
};
